// Cart-drawer upsell: loads Shopify product recommendations for the first cart
// item via the Section Rendering API and adds products without leaving the drawer.
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('cart:update', this.#onCartUpdate);

    // The header section is hydrated (re-rendered + morphed) shortly after
    // load, and cart updates morph it too — both wipe the client-loaded cards
    // back to the empty server state. Watch our subtree and repopulate.
    this.observer = new MutationObserver(() => {
      const dynamic = this.querySelector('[data-cart-upsell-dynamic]');
      if (dynamic && !dynamic.children.length) this.#scheduleReload();
    });
    this.observer.observe(this, { childList: true, subtree: true });

    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    this.observer?.disconnect();
    clearTimeout(this.reloadTimer);
  }

  // After a cart update the section may be morphed back to its server-rendered
  // state (hidden, no dynamic cards), and the first cart item may have changed.
  // Reload once the morph has settled.
  #onCartUpdate = () => {
    this.#scheduleReload();
  };

  #scheduleReload() {
    clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => this.#loadRecommendations(), 350);
  }

  async #loadRecommendations() {
    const { recommendationsUrl, productId, sectionId, intent } = this.dataset;
    this.dynamic = this.querySelector('[data-cart-upsell-dynamic]');
    if (!recommendationsUrl || !productId || !this.dynamic) return;

    const url = `${recommendationsUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const cards = parsed.querySelector('[data-cart-upsell-cards]');

      if (cards && cards.children.length) {
        this.dynamic.replaceChildren(cards);
        this.removeAttribute('hidden');
      }
    } catch (error) {
      console.error('Cart upsell recommendations error:', error);
    }
  }

  #onClick = (event) => {
    const button = event.target.closest('.cart-upsell__add');
    if (button) this.#add(button);
  };

  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');

    const cartItems = document.querySelector('cart-items-component');
    const cartSectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : null;

    const body = { items: [{ id: variantId, quantity: 1 }] };
    if (cartSectionId) {
      body.sections = cartSectionId;
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);

      // Triggers cart-items-component to re-render the cart section (morph when
      // sections are provided, otherwise a fresh section fetch).
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { source: 'cart-upsell', data: { sections: data.sections } },
        })
      );
    } catch (error) {
      console.error('Cart upsell add error:', error);
      button.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
